import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import {
  AppFile,
  BankReference,
  CreditApplicationCreate,
  CreditApplicationFieldsValues,
  CreditApplicationType,
  InvitedSignatures,
} from '@dill/dill-shared';
import { firebaseLargeFileUploadHelper } from '../../utils/helpers';

export const createCreditApplication = createAsyncThunk(
  'creditApplications/createCreditApplication',
  async (
    {
      supplierId,
      templateId,
      fieldValues,
      invitedSignatures,
      status,
      applicationType,
      senderEmail,
      bankReference,
      manuallyAddedBy,
    }: {
      supplierId: string;
      templateId: string;
      fieldValues: CreditApplicationFieldsValues[];
      invitedSignatures: InvitedSignatures[];
      status:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'SUBMITTED'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'READY_FOR_DECISION';
      applicationType: CreditApplicationType;
      senderEmail: string;
      bankReference: BankReference[] | null;
      manuallyAddedBy?: { id?: string; name?: string; email?: string };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Token not valid.');
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/createCreditApplication`,
        {
          supplierId,
          templateId,
          fieldValues,
          invitedSignatures,
          status,
          applicationType,
          senderEmail,
          bankReference,
          manuallyAddedBy,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const saveDraftCreditApplication = createAsyncThunk(
  'creditApplications/saveDraftCreditApplication',
  async (
    {
      creditApplicationDetails,
      creditApplicationId,
    }: {
      creditApplicationDetails: CreditApplicationCreate;
      creditApplicationId: string | null;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Token not valid.');
    }
    try {
      let response = null;
      if (creditApplicationId) {
        response = await axios.put<AppServerResponse>(
          `${BASE_API_URL}/creditApplications/updateCreditApplication/${creditApplicationId}`,
          { ...creditApplicationDetails },
          {
            headers: {},
          }
        );
      } else {
        response = await axios.post<AppServerResponse>(
          `${BASE_API_URL}/creditApplications/saveDraftCreditApplication`,
          { ...creditApplicationDetails },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getDraftCreditApplication = createAsyncThunk(
  'creditApplications/getDraftCreditApplication',
  async (
    {
      email,
      applicationType,
    }: {
      email: string;
      applicationType?: CreditApplicationType;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Token not valid.');
    }
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getDraftCreditApplication`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            email,
            ...(applicationType && { applicationType }),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCustomerApplications = createAsyncThunk(
  'creditApplications/getCustomerApplications',
  async (
    {
      email,
      applicationType,
      isArchived,
    }: {
      email: string;
      applicationType?: CreditApplicationType;
      isArchived?: boolean;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return thunkApi.rejectWithValue('Token not valid.');
    }
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getCustomerApplications`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            email,
            ...(applicationType && { applicationType }),
            ...(isArchived !== undefined && { isArchived }),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditApplications = createAsyncThunk(
  'creditApplications/getCreditApplications',
  async ({ supplierId, isArchived }: { supplierId: string; isArchived?: boolean }, thunkApi) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getCreditApplications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            supplierId,
            ...(isArchived !== undefined && { isArchived }),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditApplication = createAsyncThunk(
  'creditApplications/getCreditApplication',
  async (
    { creditApplicationId, hashedType }: { creditApplicationId: string; hashedType?: string },
    thunkApi
  ) => {
    let additionalData = {};
    if (hashedType) {
      additionalData = { hashedType };
    }
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getCreditApplication/${creditApplicationId}`,
        {
          headers: {},
          params: {
            ...additionalData,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateCreditApplication = createAsyncThunk(
  'creditApplications/updateCreditApplication',
  async (
    {
      creditApplicationId,
      fieldValues,
      invitedSignature,
      invitedSignatures,
      status,
      templateId,
      supplierId,
      senderEmail,
      bankReference,
      assignedReviewerUserId,
      feedbackFields,
      savingType,
      comment,
      signedInvitedSignature,
      metaData,
      applicationHistoryId,
      preserveSignatures,
      isFeedbackResponse,
    }: {
      creditApplicationId: string;
      fieldValues?: CreditApplicationFieldsValues[];
      invitedSignature?: InvitedSignatures;
      invitedSignatures?: InvitedSignatures[];
      preserveSignatures?: boolean;
      // Set when the customer saves from the "Send Feedback" link, so open feedback moves to review.
      isFeedbackResponse?: boolean;
      // Frontend-only flag, never sent to the backend (see updateCreditApplicationExtraReducer).
      // Set this on background/side-effect updates that don't carry the applicant's current
      // fieldValues (e.g. the auto SENT->OPENED status ping) so the response — which reflects
      // the document as it was in the DB at read time, not whatever the user has typed locally
      // since — doesn't get merged into state.creditApplication and silently clobber in-progress
      // local edits via the components that re-sync from it.
      skipCreditApplicationSync?: boolean;
      status?:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'SUBMITTED'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'INTERNALLY_REJECTED'
        | 'READY_FOR_DECISION';
      templateId?: string;
      supplierId?: string;
      assignedReviewerUserId?: string | null;
      senderEmail?: string;
      bankReference?: BankReference[] | null;
      feedbackFields?: { name?: string; id: string; uniqueId: string }[];
      savingType?: string;
      comment?: string;
      signedInvitedSignature?: InvitedSignatures[];
      metaData?: any;
      applicationHistoryId?: string | null;
    },
    thunkApi
  ) => {
    try {
      let updateDetails = {};
      if (fieldValues) {
        updateDetails = { ...updateDetails, fieldValues };
      }
      if (invitedSignature) {
        updateDetails = { ...updateDetails, invitedSignature };
      }
      if (status) {
        updateDetails = { ...updateDetails, status };
      }
      if (templateId) {
        updateDetails = { ...updateDetails, templateId };
      }
      if (supplierId) {
        updateDetails = { ...updateDetails, supplierId };
      }
      if (invitedSignatures) {
        updateDetails = { ...updateDetails, invitedSignatures };
      }
      if (senderEmail) {
        updateDetails = { ...updateDetails, senderEmail };
      }
      if (bankReference) {
        updateDetails = { ...updateDetails, bankReference };
      }
      if (assignedReviewerUserId !== undefined) {
        updateDetails = { ...updateDetails, assignedReviewerUserId };
      }
      if (feedbackFields) {
        updateDetails = { ...updateDetails, feedbackFields };
      }
      if (savingType) {
        updateDetails = { ...updateDetails, savingType };
      }
      if (comment) {
        updateDetails = { ...updateDetails, comment };
      }
      if (signedInvitedSignature) {
        updateDetails = { ...updateDetails, signedInvitedSignature };
      }
      if (metaData) {
        updateDetails = { ...updateDetails, metaData };
      }
      if (preserveSignatures) {
        updateDetails = { ...updateDetails, preserveSignatures };
      }
      if (isFeedbackResponse) {
        updateDetails = { ...updateDetails, isFeedbackResponse };
      }

      if (applicationHistoryId) {
        const response = await axios.put<AppServerResponse>(
          `${BASE_API_URL}/creditApplications/updateDataCreditApplicationHistory/${applicationHistoryId}`,
          { ...updateDetails },
          { headers: {} }
        );
        return response.data;
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/updateCreditApplication/${creditApplicationId}`,
        { ...updateDetails },
        {
          headers: {},
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierCreditApplication = createAsyncThunk(
  'creditApplications/getSupplierCreditApplication',
  async (
    {
      creditApplicationId,
      supplierId,
      hashedType,
      fetchType,
      skipLoading,
      skipCache,
    }: {
      creditApplicationId: string;
      supplierId: string;
      hashedType?: string;
      fetchType?: string;
      skipLoading?: string;
      skipCache?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getCreditApplication/${creditApplicationId}`,
        {
          headers: { Authorization: 'Bearer ' + token },
          params: {
            hashedType,
            ...(fetchType ? { fetchType } : {}),
            ...(skipLoading ? { skipLoading } : {}),
            ...(skipCache ? { skipCache } : {}),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditApplicationPDF = createAsyncThunk(
  'creditApplications/getCreditApplicationPDF',
  async (
    {
      supplierId,
      creditApplicationId,
      preview = false,
    }: { supplierId: string; creditApplicationId: string; preview?: boolean },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getCreditApplicationPDF/${creditApplicationId}`,
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      if (!preview) {
        link.download = `credit-application-${creditApplicationId}-${+new Date()}.pdf`;
        link.click();
      }

      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendReferencesCreditApplication = createAsyncThunk(
  'creditApplications/sendReferencesCreditApplication',
  async (
    {
      creditApplicationId,
      fieldValues,
      supplierId,
      sendReferenceEmail,
      applicationHistoryId,
    }: {
      creditApplicationId: string;
      fieldValues: CreditApplicationFieldsValues[];
      supplierId: string;
      sendReferenceEmail: 'initial' | 'followUp';
      applicationHistoryId?: string | null;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) return null;

      const endpoint = applicationHistoryId
        ? `updateCreditApplicationHistory/${applicationHistoryId}`
        : `updateCreditApplication/${creditApplicationId}`;

      const url = `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/${endpoint}`;

      const response = await axios.put<AppServerResponse>(
        url,
        { fieldValues, sendReferenceEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error: any) {
      console.error(error);

      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }

      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierCreditApplication = createAsyncThunk(
  'creditApplications/updateSupplierCreditApplication',
  async (
    {
      creditApplicationId,
      fieldValues,
      invitedSignature,
      status,
      templateId,
      supplierId,
      tradeReferenceResponse,
      amountAproved,
      applicationType,
      assignedReviewerUserId,
      senderEmail,
      bankReference,
      emailsToNotify,
      rejectReason,
      companyNACMID,
      internalNotes,
      notes,
      isDeleted,
      branchId,
      assignedSalesRep,
      customerAccountNumber,
      savingType,
      comment,
      isArchived,
      terms,
      additionalMessage,
      ballInCourtId,
      specificRules,
      oneOffMessageOverrideHtml,
      oneOffMessageOverrideSubject,
      preserveSignatures,
    }: {
      creditApplicationId: string;
      fieldValues?: CreditApplicationFieldsValues[];
      invitedSignature?: InvitedSignatures;
      status?:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'SUBMITTED'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'INTERNALLY_REJECTED'
        | 'READY_FOR_DECISION';
      templateId?: string;
      supplierId: string;
      preserveSignatures?: boolean;
      tradeReferenceResponse?: Record<
        string,
        { textResponse?: string | null; files?: AppFile[] | null }
      >;
      amountAproved?: number;
      applicationType?: CreditApplicationType;
      assignedReviewerUserId?: string | null;
      senderEmail?: string;
      bankReference?: BankReference[] | null;
      emailsToNotify?: string[];
      rejectReason?: string;
      companyNACMID?: string;
      internalNotes?: {
        note: string;
        date: Date;
        submittedBy?: string | null;
        visibleToSales?: boolean;
        fromSales?: boolean;
        mentionedUserIds?: string[];
      }[];
      notes?: string;
      isDeleted?: boolean;
      branchId?: string;
      assignedSalesRep?: string[];
      customerAccountNumber?: string;
      savingType?: string;
      comment?: string;
      isArchived?: boolean;
      terms?: string;
      additionalMessage?: string;
      ballInCourtId?: string | null;
      specificRules?: {
        fieldId: string;
        isRequired?: boolean;
        minimumNumberOfGroups?: number;
        minimumNumberOfSignatures?: number;
        minimumCreditAmount?: number;
        hidden?: boolean;
      }[];
      oneOffMessageOverrideHtml?: string;
      oneOffMessageOverrideSubject?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      let updateDetails = {};
      if (fieldValues) {
        updateDetails = { ...updateDetails, fieldValues };
      }
      if (invitedSignature) {
        updateDetails = { ...updateDetails, invitedSignature };
      }
      if (status) {
        updateDetails = { ...updateDetails, status };
      }
      if (templateId) {
        updateDetails = { ...updateDetails, templateId };
      }
      if (supplierId) {
        updateDetails = { ...updateDetails, supplierId };
      }
      if (tradeReferenceResponse) {
        updateDetails = { ...updateDetails, tradeReferenceResponse };
      }
      if (amountAproved) {
        updateDetails = { ...updateDetails, amountAproved };
      }
      if (applicationType) {
        updateDetails = { ...updateDetails, applicationType };
      }
      if (assignedReviewerUserId !== undefined) {
        updateDetails = { ...updateDetails, assignedReviewerUserId };
      }
      if (senderEmail) {
        updateDetails = { ...updateDetails, senderEmail };
      }
      if (bankReference) {
        updateDetails = { ...updateDetails, bankReference };
      }
      if (emailsToNotify) {
        updateDetails = { ...updateDetails, emailsToNotify };
      }
      if (rejectReason) {
        updateDetails = { ...updateDetails, rejectReason };
      }
      if (oneOffMessageOverrideHtml) {
        updateDetails = { ...updateDetails, oneOffMessageOverrideHtml };
      }
      if (oneOffMessageOverrideSubject) {
        updateDetails = { ...updateDetails, oneOffMessageOverrideSubject };
      }
      if (companyNACMID) {
        updateDetails = { ...updateDetails, companyNACMID };
      }
      if (internalNotes) {
        updateDetails = { ...updateDetails, internalNotes };
      }
      if (isDeleted) {
        updateDetails = { ...updateDetails, isDeleted };
      }
      if (branchId) {
        updateDetails = { ...updateDetails, branchId };
      }
      if (assignedSalesRep) {
        updateDetails = { ...updateDetails, assignedSalesRep };
      }
      if (notes) {
        updateDetails = { ...updateDetails, notes };
      }
      if (customerAccountNumber) {
        updateDetails = { ...updateDetails, customerAccountNumber };
      }
      if (savingType) {
        updateDetails = { ...updateDetails, savingType };
      }
      if (comment) {
        updateDetails = { ...updateDetails, comment };
      }
      if (isArchived !== undefined) {
        updateDetails = { ...updateDetails, isArchived };
      }
      if (terms) {
        updateDetails = { ...updateDetails, terms };
      }
      if (additionalMessage) {
        updateDetails = { ...updateDetails, additionalMessage };
      }
      if (ballInCourtId !== undefined) {
        updateDetails = { ...updateDetails, ballInCourtId };
      }
      if (specificRules) {
        updateDetails = { ...updateDetails, specificRules };
      }
      if (preserveSignatures !== undefined) {
        updateDetails = { ...updateDetails, preserveSignatures };
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/updateCreditApplication/${creditApplicationId}`,
        { ...updateDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const editCreditApplicationData = createAsyncThunk(
  'creditApplications/editCreditApplicationData',
  async (
    {
      creditApplicationId,
      supplierId,
      fieldValues,
    }: {
      creditApplicationId: string;
      supplierId: string;
      fieldValues: CreditApplicationFieldsValues[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.patch<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/editCreditApplicationData/${creditApplicationId}`,
        { fieldValues },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const createManualUploadCreditApplication = createAsyncThunk(
  'creditApplications/createManualUploadCreditApplication',
  async (
    {
      supplierId,
      templateId,
      fieldValues,
      invitedSignatures,
      status,
      applicationType,
      senderEmail,
      requestedDate,
      selectedPdfFile,
      bankReference,
    }: {
      supplierId: string;
      templateId: string;
      fieldValues: CreditApplicationFieldsValues[];
      invitedSignatures: InvitedSignatures[];
      status: 'APPROVED' | 'SUBMITTED';
      applicationType: CreditApplicationType;
      senderEmail: string;
      requestedDate: string;
      selectedPdfFile: { name: string; url: string } | null;
      bankReference: BankReference[] | null;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    let additionalDetails = {};
    if (selectedPdfFile) {
      const downloadUrl = await firebaseLargeFileUploadHelper({
        blobUrl: selectedPdfFile.url,
        fileNameWithExtension: selectedPdfFile.name,
        storagePath: `creditApplicationFiles/${supplierId}/${Date.now()}`,
      });

      if (downloadUrl) {
        additionalDetails = {
          ...additionalDetails,
          applicationPdf: {
            name: selectedPdfFile.name,
            url: downloadUrl,
            fileType: selectedPdfFile.name.split('.').pop() || '',
          },
        };
      }
    }
    if (bankReference) {
      additionalDetails = {
        ...additionalDetails,
        bankReference,
      };
    }
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/createManualUploadCreditApplication`,
        {
          supplierId,
          templateId,
          fieldValues,
          invitedSignatures,
          status,
          applicationType,
          senderEmail,
          requestedDate,
          ...additionalDetails,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

const postSupplierCreditApplicationAction = async (
  path: string,
  supplierId: string,
  body: Record<string, unknown>,
  thunkApi: { dispatch: any; rejectWithValue: (value: unknown) => any }
) => {
  const token = await myFirebaseAuth.currentUser?.getIdToken();
  if (!token) {
    return null;
  }
  try {
    const response = await axios.post<AppServerResponse>(
      `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/${path}`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.log(error?.message);
    if (error?.response?.status === 401) {
      thunkApi.dispatch(logoutUser());
    }
    return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error.message);
  }
};

// Tax exemption team's Yes/No on the customer's uploaded tax exemption file. Completes every
// review task for the application.
export const decideTaxExemptReview = createAsyncThunk(
  'creditApplications/decideTaxExemptReview',
  async (
    {
      supplierId,
      creditApplicationId,
      decision,
    }: { supplierId: string; creditApplicationId: string; decision: 'EXEMPT' | 'NOT_EXEMPT' },
    thunkApi
  ) =>
    postSupplierCreditApplicationAction(
      'decideTaxExemptReview',
      supplierId,
      { creditApplicationId, decision },
      thunkApi
    )
);

// Sets the customer's tax exempt answer to No after the tax exemption team said not exempt.
export const applyTaxExemptNotExempt = createAsyncThunk(
  'creditApplications/applyTaxExemptNotExempt',
  async (
    { supplierId, creditApplicationId }: { supplierId: string; creditApplicationId: string },
    thunkApi
  ) =>
    postSupplierCreditApplicationAction(
      'applyTaxExemptNotExempt',
      supplierId,
      { creditApplicationId },
      thunkApi
    )
);

export const resolveFeedbackCreditApplication = createAsyncThunk(
  'creditApplications/resolveFeedbackCreditApplication',
  async (
    {
      supplierId,
      creditApplicationId,
      applicationHistoryId,
      feedbackDate,
    }: {
      supplierId: string;
      creditApplicationId: string;
      applicationHistoryId?: string | null;
      // ISO date of the feedback entry to resolve; omit to resolve all open feedback.
      feedbackDate?: string;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/resolveFeedbackCreditApplication`,
        {
          creditApplicationId,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
          ...(feedbackDate ? { feedbackDate } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendFeedbackCreditApplication = createAsyncThunk(
  'creditApplications/sendFeedbackCreditApplication',
  async (
    {
      supplierId,
      feedback,
      creditApplicationId,
      fields,
      applicationHistoryId,
      specificRules,
    }: {
      supplierId: string;
      creditApplicationId: string;
      feedback: string;
      fields: {
        name?: string;
        id: string;
        uniqueId: string;
      }[];
      applicationHistoryId?: string | null;
      specificRules?: {
        fieldId: string;
        isRequired?: boolean;
        minimumNumberOfGroups?: number;
        minimumNumberOfSignatures?: number;
        minimumCreditAmount?: number;
        hidden?: boolean;
      }[];
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/sendFeedbackCreditApplication`,
        {
          feedback,
          creditApplicationId,
          fields,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
          ...(specificRules && specificRules.length > 0 ? { specificRules } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendCreditReportNotification = createAsyncThunk(
  'creditApplications/sendCreditReportNotification',
  async (
    {
      message,
      sender,
      creditApplicationId,
      supplierId,
    }: {
      message: string;
      sender: string;
      creditApplicationId: string;
      supplierId: string;
    },
    thunkApi
  ) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/sendCreditReportNotification`,
        {
          message,
          sender,
          creditApplicationId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendCreditApplicationTemplateLink = createAsyncThunk(
  'creditApplications/sendCreditApplicationTemplateLink',
  async (
    {
      supplierId,
      firstName,
      lastName,
      email,
      groupedSpecialConditions,
      assignedSalesRep,
      comment,
      attachments,
      templateId,
      specificRules,
      branchId,
    }: {
      supplierId: string;
      firstName: string;
      lastName: string;
      email: string;
      groupedSpecialConditions?: { name: string; minimumNumber: number }[];
      assignedSalesRep?: string[];
      comment?: string;
      attachments?: { url: string; name: string }[];
      templateId?: string;
      // External forms: the branch picked when sending (else the sender's own branch).
      branchId?: string;
      specificRules?: {
        fieldId: string;
        isRequired?: boolean;
        minimumNumberOfGroups?: number;
        minimumNumberOfSignatures?: number;
        minimumCreditAmount?: number;
        hidden?: boolean;
      }[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let details = {};
      if (groupedSpecialConditions) {
        details = {
          ...details,
          groupedSpecialConditions,
        };
      }
      if (assignedSalesRep) {
        details = {
          ...details,
          assignedSalesRep,
        };
      }
      if (comment) {
        details = {
          ...details,
          comment,
        };
      }
      if (attachments) {
        details = {
          ...details,
          attachments,
        };
      }
      if (branchId) {
        details = { ...details, branchId };
      }
      if (templateId) {
        details = {
          ...details,
          templateId,
        };
      }
      if (specificRules && specificRules.length > 0) {
        details = {
          ...details,
          specificRules,
        };
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/sendCreditApplicationTemplateLink`,
        { firstName, lastName, email, supplierId, ...details },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const requestUpdateCreditApplication = createAsyncThunk(
  'creditApplications/requestUpdateCreditApplication',
  async (
    {
      supplierId,
      applicationId,
      email,
      comment,
    }: {
      supplierId: string;
      applicationId: string;
      email: string;
      comment?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/requestUpdateCreditApplication`,
        { applicationId, email, ...(comment && { comment }) },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createApplicationUpdateHistory = createAsyncThunk(
  'creditApplications/createApplicationUpdateHistory',
  async (
    {
      supplierId,
      templateId,
      fieldValues,
      invitedSignatures,
      status,
      applicationType,
      senderEmail,
      bankReference,
      applicationId,
      assignedReviewerUserId,
      amountAproved,
    }: {
      supplierId: string;
      templateId: string;
      fieldValues: CreditApplicationFieldsValues[];
      invitedSignatures: InvitedSignatures[];
      status:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'SUBMITTED'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'READY_FOR_DECISION';
      applicationType: CreditApplicationType;
      senderEmail: string;
      bankReference: BankReference[] | null;
      applicationId: string;
      assignedReviewerUserId?: string | null;
      amountAproved?: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return thunkApi.rejectWithValue('Token not valid.');
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/createApplicationUpdateHistory`,
        {
          supplierId,
          templateId,
          fieldValues,
          invitedSignatures,
          status,
          applicationType,
          senderEmail,
          bankReference,
          applicationId,
          ...(assignedReviewerUserId ? { assignedReviewerUserId } : {}),
          ...(amountAproved ? { amountAproved } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateApplicationUpdateHistory = createAsyncThunk(
  'creditApplications/updateApplicationUpdateHistory',
  async (
    {
      creditApplicationId,
      fieldValues,
      invitedSignature,
      invitedSignatures,
      status,
      templateId,
      supplierId,
      tradeReferenceResponse,
      amountAproved,
      applicationType,
      assignedReviewerUserId,
      senderEmail,
      bankReference,
      applicationHistoryId,
      companyNACMID,
      internalNotes,
      isDeleted,
      branchId,
      assignedSalesRep,
      isArchived,
      savingType,
      preserveSignatures,
      isFeedbackResponse,
    }: {
      creditApplicationId: string;
      fieldValues?: CreditApplicationFieldsValues[];
      invitedSignature?: InvitedSignatures;
      invitedSignatures?: InvitedSignatures[];
      status?:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'SUBMITTED'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'INTERNALLY_REJECTED'
        | 'READY_FOR_DECISION';
      templateId?: string;
      supplierId: string;
      tradeReferenceResponse?: Record<
        string,
        { textResponse?: string | null; files?: AppFile[] | null }
      >;
      amountAproved?: number;
      applicationType?: CreditApplicationType;
      assignedReviewerUserId?: string | null;
      senderEmail?: string;
      bankReference?: BankReference[] | null;
      applicationHistoryId: string;
      companyNACMID?: string;
      internalNotes?: {
        note: string;
        date: Date;
        submittedBy?: string | null;
        visibleToSales?: boolean;
        fromSales?: boolean;
        mentionedUserIds?: string[];
      }[];
      isDeleted?: boolean;
      branchId?: string;
      assignedSalesRep?: string[];
      isArchived?: boolean;
      savingType?: string;
      preserveSignatures?: boolean;
      isFeedbackResponse?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      let updateDetails = {};
      if (fieldValues) {
        updateDetails = { ...updateDetails, fieldValues };
      }
      if (invitedSignature) {
        updateDetails = { ...updateDetails, invitedSignature };
      }
      if (invitedSignatures) {
        updateDetails = { ...updateDetails, invitedSignatures };
      }
      if (status) {
        updateDetails = { ...updateDetails, status };
      }
      if (templateId) {
        updateDetails = { ...updateDetails, templateId };
      }
      if (supplierId) {
        updateDetails = { ...updateDetails, supplierId };
      }
      if (tradeReferenceResponse) {
        updateDetails = { ...updateDetails, tradeReferenceResponse };
      }
      if (amountAproved) {
        updateDetails = { ...updateDetails, amountAproved };
      }
      if (applicationType) {
        updateDetails = { ...updateDetails, applicationType };
      }
      if (assignedReviewerUserId !== undefined) {
        updateDetails = { ...updateDetails, assignedReviewerUserId };
      }
      if (senderEmail) {
        updateDetails = { ...updateDetails, senderEmail };
      }
      if (bankReference) {
        updateDetails = { ...updateDetails, bankReference };
      }
      if (companyNACMID) {
        updateDetails = { ...updateDetails, companyNACMID };
      }
      if (internalNotes) {
        updateDetails = { ...updateDetails, internalNotes };
      }
      if (isDeleted) {
        updateDetails = { ...updateDetails, isDeleted };
      }
      if (branchId) {
        updateDetails = { ...updateDetails, branchId };
      }
      if (assignedSalesRep) {
        updateDetails = { ...updateDetails, assignedSalesRep };
      }
      if (isArchived !== undefined) {
        updateDetails = { ...updateDetails, isArchived };
      }
      if (savingType) {
        updateDetails = { ...updateDetails, savingType };
      }
      if (preserveSignatures !== undefined) {
        updateDetails = { ...updateDetails, preserveSignatures };
      }
      if (isFeedbackResponse) {
        updateDetails = { ...updateDetails, isFeedbackResponse };
      }
      updateDetails = { ...updateDetails, creditApplicationId };

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/updateCreditApplicationHistory/${applicationHistoryId}`,
        { ...updateDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getApplicationHistory = createAsyncThunk(
  'creditApplications/getApplicationHistory',
  async (
    { creditApplicationId, hashedType }: { creditApplicationId: string; hashedType?: string },
    thunkApi
  ) => {
    let additionalData = {};
    if (hashedType) {
      additionalData = { hashedType };
    }
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getApplicationHistory/${creditApplicationId}`,
        {
          headers: {},
          params: {
            ...additionalData,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const resendRequestCreditAppSignature = createAsyncThunk(
  'creditApplications/resendRequestCreditAppSignature',
  async (
    {
      supplierId,
      creditApplicationId,
      invitedSignature,
      comment,
    }: {
      supplierId: string;
      creditApplicationId: string;
      invitedSignature: InvitedSignatures;
      comment?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/resendRequestCreditAppSignature`,
        {
          invitedSignature,
          creditApplicationId,
          ...(comment && { comment }),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return { success: true, data: { response } };
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const requestSignatureDetailsUpdate = createAsyncThunk(
  'creditApplications/requestSignatureDetailsUpdate',
  async (
    {
      supplierId,
      creditApplicationId,
      invitedSignature,
      comment,
    }: {
      supplierId: string;
      creditApplicationId: string;
      invitedSignature: InvitedSignatures;
      comment?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/requestSignatureDetailsUpdate`,
        {
          invitedSignature,
          creditApplicationId,
          ...(comment && { comment }),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return { success: true, data: { response } };
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const resendCreditApplicationLink = createAsyncThunk(
  'creditApplications/resendCreditApplicationLink',
  async (
    {
      supplierId,
      creditApplicationId,
      comment,
    }: {
      supplierId: string;
      creditApplicationId: string;
      comment?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/resendCreditApplicationLink`,
        { creditApplicationId, ...(comment && { comment }) },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const makeCreditAppSignature = createAsyncThunk(
  'creditApplications/makeCreditAppSignature',
  async (
    {
      supplierId,
      creditApplicationId,
      invitedSignature,
      notes,
    }: {
      supplierId: string;
      creditApplicationId: string;
      invitedSignature: InvitedSignatures;
      notes?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let additionalData = {};
      if (notes) {
        additionalData = { ...additionalData, notes };
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/makeCreditAppSignature`,
        {
          invitedSignature,
          creditApplicationId,
          ...additionalData,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const refreshPlaidReportApplication = createAsyncThunk(
  'creditApplications/refreshPlaidReportApplication',
  async (
    { creditApplicationId, supplierId }: { creditApplicationId: string; supplierId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/refreshPlaidReportApplication/${creditApplicationId}`,
        {
          headers: { Authorization: 'Bearer ' + token },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const internalApproveCreditApplication = createAsyncThunk(
  'creditApplications/internalApproveCreditApplication',
  async (
    {
      creditApplicationId,
      status,
      supplierId,
      amountAproved,
      emailsToNotify,
      rejectReason,
      terms,
      applicationHistoryId,
      customerAccountNumber,
      billToAccountNumber,
      subsidiaryId,
      approvalAdditionalFieldValues,
      customerOnApprovalAction,
      preserveExistingAccountDetails,
    }: {
      creditApplicationId: string;
      status?:
        | 'SENT'
        | 'OPENED'
        | 'DRAFT'
        | 'SUBMITTED'
        | 'AWAITING_SIGNATURE'
        | 'INTERNAL_SIGNATURE'
        | 'APPROVED'
        | 'REJECTED'
        | 'APPROVED_COD'
        | 'APPLICATION_UPDATED'
        | 'FEEDBACK_SENT'
        | 'INTERNALLY_APPROVED'
        | 'INTERNALLY_APPROVED_COD'
        | 'INTERNALLY_REJECTED'
        | 'READY_FOR_DECISION';
      supplierId: string;
      amountAproved?: number;
      emailsToNotify?: string[];
      rejectReason?: string;
      terms?: string;
      applicationHistoryId?: string | null;
      approvalAdditionalFieldValues?: {
        id: string;
        name: string;
        erpId?: string;
        value?: string;
      }[];
      customerAccountNumber?: string;
      // Eclipse "update": the existing Bill-To the Ship-To (customerAccountNumber) belongs to.
      billToAccountNumber?: string;
      subsidiaryId?: string;
      customerOnApprovalAction?: 'create' | 'update';
      preserveExistingAccountDetails?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      let updateDetails = {};
      if (status) {
        updateDetails = { ...updateDetails, status };
      }
      if (supplierId) {
        updateDetails = { ...updateDetails, supplierId };
      }
      if (amountAproved) {
        updateDetails = { ...updateDetails, amountAproved };
      }
      if (emailsToNotify) {
        updateDetails = { ...updateDetails, emailsToNotify };
      }
      if (rejectReason) {
        updateDetails = { ...updateDetails, rejectReason };
      }
      if (terms) {
        updateDetails = { ...updateDetails, terms };
      }
      if (approvalAdditionalFieldValues && approvalAdditionalFieldValues.length > 0) {
        updateDetails = { ...updateDetails, approvalAdditionalFieldValues };
      }
      if (customerAccountNumber) {
        updateDetails = { ...updateDetails, customerAccountNumber };
      }
      if (billToAccountNumber) {
        updateDetails = { ...updateDetails, billToAccountNumber };
      }
      if (subsidiaryId) {
        updateDetails = { ...updateDetails, subsidiaryId };
      }
      if (customerOnApprovalAction) {
        updateDetails = { ...updateDetails, customerOnApprovalAction };
      }
      if (typeof preserveExistingAccountDetails === 'boolean') {
        updateDetails = { ...updateDetails, preserveExistingAccountDetails };
      }
      if (approvalAdditionalFieldValues && Object.keys(approvalAdditionalFieldValues).length > 0) {
        updateDetails = { ...updateDetails, approvalAdditionalFieldValues };
      }

      const endpoint = applicationHistoryId
        ? `internalApproveApplicationHistory/${applicationHistoryId}`
        : `internalApproveCreditApplication/${creditApplicationId}`;

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/${endpoint}`,
        { ...updateDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const updateBankReferenceResponse = createAsyncThunk(
  'creditApplications/updateBankReferenceResponse',
  async (
    {
      supplierId,
      creditApplicationId,
      bankReference,
      applicationHistoryId,
    }: {
      supplierId: string;
      creditApplicationId: string;
      bankReference: BankReference;
      applicationHistoryId?: string | null;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/updateBankReferenceResponse/${creditApplicationId}`,
        {
          bankReference,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendBankReference = createAsyncThunk(
  'creditApplications/sendBankReference',
  async (
    {
      supplierId,
      creditApplicationId,
      bankReference,
      emailType,
      applicationHistoryId,
    }: {
      supplierId: string;
      creditApplicationId: string;
      bankReference: BankReference;
      emailType: 'initial' | 'followUp';
      applicationHistoryId?: string | null;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/sendBankReference/${creditApplicationId}`,
        {
          bankReference,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: { emailType },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const shareApplication = createAsyncThunk(
  'creditApplications/shareApplication',
  async (
    {
      supplierId,
      creditApplicationId,
      email,
      message,
      applicationHistoryId,
    }: {
      supplierId: string;
      creditApplicationId: string;
      email: string;
      message: string;
      applicationHistoryId?: string | null;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/shareApplication/${creditApplicationId}`,
        {
          email,
          message,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

// Plain request rather than a thunk: the review-and-sign page is public and only needs this flag.
export const fetchCreditApplicationSignatureSettings = async (supplierId: string) => {
  const response = await axios.get<AppServerResponse>(
    `${BASE_API_URL}/creditApplications/getCreditApplicationSignatureSettings/${supplierId}`
  );
  return response.data.data as { isTypedSignatureDisabled: boolean };
};
